import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  Fragment,
} from 'react'
import classNames from 'classnames'
import { Spinner } from 'react-bootstrap'
import { useLocation } from 'react-router-dom'

import PdfCard from '../ui/PdfCard/PdfCard'
import Paginator from '../Paginator/Paginator'
import PressReleaseFilter from './MediaPdfsFilter'
import ErrorComponent from '../ErrorComponent/ErrorComponent'
import DataNotfound from '../DataNotfound/DataNotfound'
import AnimatedImageDescriptionCard from '../AnimatedImageDescriptionCard/AnimatedImageDescriptionCard'
import useFetchFilteredData from '../../hooks/useFetchFilteredData'
import useIsMobile from '../../hooks/useIsMobile'
import { getImageUrl, getTotalPages, isArabic } from '../../utils'
import { mediaParamList } from '../../Contansts'
import Markdown from '../Markdown/Markdown'

import styles from './MediaPdfsLayout.module.scss'
import { useLanguage } from '../../context/LangaugeContextProvider/LangaugeContextProvider'

function MediaPdfsLayout({
  data,
  pagination: initialPagination,
  emptyMessage,
  onShow,
}) {
  const { pathname } = useLocation()
  const path = pathname.split('/')
  const currentSectionPath = path[path.length - 1]
  const url = mediaParamList[currentSectionPath]
  const { language } = useLanguage()

  const [selectedFilter, setSelectedFilter] = useState({
    selectedYear: null,
    selectedpdfType: null,
    selectedSortType: null,
  })

  const [totalPages, setTotalPages] = useState(getTotalPages(initialPagination))
  const [currentPage, setCurrentPage] = useState(1)
  const loaderRef = useRef(null)
  const [cards, setCards] = useState([])
  const isMobile = useIsMobile()
  const [filtersList, setfiltersList] = useState()
  const [initialLoading, setInitialLoading] = useState(true)

  const {
    data: filteredData,
    loading,
    error,
    setParams,
  } = useFetchFilteredData(url)

  useEffect(() => {
    const { year_filter, pdf_type, sort } = data || {}
    let filter = {
      selectedYear: year_filter?.list?.length ? year_filter?.list[0] : null,
      selectedpdfType: pdf_type?.list?.length ? pdf_type?.list[0] : null,
      selectedSortType: sort?.list?.length ? sort?.list[0] : null,
    }
    setSelectedFilter(filter)
    setfiltersList(data)

    const params = `year_filter=${filter?.selectedYear?.value}&pdf_type=${filter?.selectedpdfType?.value}&sortOrder=${filter?.selectedSortType?.value}&page=${currentPage}`
    setParams(params)
  }, [data])

  useEffect(() => {
    if (filteredData) {
      setInitialLoading(false)
      const { result = {}, pagination } = filteredData?.data || {}
      if (pagination) {
        setTotalPages(getTotalPages(pagination))
      }
      if (isMobile) {
        const pdfs = result?.sectiondata?.pdfs || []
        setCards((prevData) => [...prevData, ...pdfs])
      } else {
        setCards(result?.sectiondata?.pdfs || [])
      }
    }
  }, [filteredData])

  const handleFilterChange = (e, option, key) => {
    if (selectedFilter?.[key]?.value !== option?.value) {
      setSelectedFilter((prevState) => ({
        ...prevState,
        [key]: {
          ...option,
        },
      }))
      setCurrentPage(1)
      setCards([])
    }
  }

  useEffect(() => {
    if (!initialLoading) {
      const params = `year_filter=${selectedFilter?.selectedYear?.value}&pdf_type=${selectedFilter?.selectedpdfType?.value}&sortOrder=${selectedFilter?.selectedSortType?.value}&page=${currentPage}`
      setParams(params)
    }
  }, [selectedFilter, currentPage])

  const lazyLoad = useCallback(
    (entries) => {
      const target = entries[0]
      if (target.isIntersecting && !loading) {
        if (currentPage < totalPages) {
          setCurrentPage((prevPage) => prevPage + 1)
        }
      }
    },
    [loading, totalPages, filteredData]
  )

  useEffect(() => {
    if (isMobile) {
      const observer = new IntersectionObserver(lazyLoad)
      if (loaderRef.current) {
        observer.observe(loaderRef.current)
      }
      return () => {
        if (loaderRef.current) {
          observer.unobserve(loaderRef.current)
        }
      }
    }
  }, [lazyLoad, cards])

  const renderPagination = () => {
    if (totalPages > 0 && cards?.length > 0) {
      return (
        <div className="items-center justify-center hidden md:flex">
          <Paginator
            currentPage={currentPage}
            numOfPage={totalPages}
            setCurrentPage={setCurrentPage}
          />
        </div>
      )
    }
  }

  const renderListItems = () => {
    const isSafeBanking = path.includes('safe-banking')
    if (cards && cards?.length > 0) {
      return (
        <div
          className={classNames(
            'grid  gap-x-6 gap-y-8 mb-8 sm:mb-0',
            path.includes('newsletter')
              ? 'grid-cols-1'
              : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
          )}
        >
          {cards?.map((card, index) => {
            return (
              <Fragment key={'mediapdf' + index}>
                {path.includes('newsletter') ? (
                  <AnimatedImageDescriptionCard
                    index={index}
                    title={card?.title}
                    image={card?.image}
                    description={card?.description}
                    isAnimated={true}
                    isView={true}
                    isDownload={true}
                    link={card?.fileinfo?.url}
                    fileName={card?.fileinfo?.name}
                    classname={styles['card-bg']}
                    // animatedBgColorClass={'off-white-bg'}
                  />
                ) : (
                  <PdfCard
                    title={card?.title}
                    link={card?.fileinfo?.url || isSafeBanking}
                    fileName={card?.fileinfo?.name}
                    isView={!isSafeBanking && card?.fileinfo?.ext !== '.zip'}
                    isDownload={!isSafeBanking}
                    icon={card?.image}
                    isShow={onShow && isSafeBanking}
                    handleShow={onShow ? () => onShow(card) : undefined}
                  />
                )}
              </Fragment>
            )
          })}
          {loading && isMobile && (
            <div className="flex justify-center">
              <Spinner />
            </div>
          )}
          <div ref={loaderRef}></div>
        </div>
      )
    }
    if (cards?.length === 0) {
      return <DataNotfound description={emptyMessage} />
    }
  }

  const renderFilteredData = () => {
    if (loading && !isMobile) {
      return (
        <div className="flex justify-center md:p-52">
          <Spinner />
        </div>
      )
    }
    if (error?.hasError) {
      return (
        <div className="flex justify-center">
          <ErrorComponent
            errorMessage={error.errorMessage}
            renderProps={() => <div>{error.errorMessage}</div>}
          />
        </div>
      )
    }

    if (!path.includes('safe-banking')) return renderListItems()
    else return renderImageDescriptionLayout()
  }

  const renderListContent = () => {
    return (
      <>
        {!path.includes('safe-banking') && (
          <div className={classNames(styles.filtersRootContainer, 'mb-10')}>
            {/* {filter_title && (
              <div className={styles['filter-title']}>{filter_title}</div>
            )} */}
            <PressReleaseFilter
              pdfTypeFilter={filtersList?.pdf_type}
              yearFilter={filtersList?.year_filter}
              sortFilter={filtersList?.sort}
              selectedFilter={selectedFilter}
              handleFilterChange={handleFilterChange}
            />
          </div>
        )}
        {renderFilteredData()}
      </>
    )
  }

  const renderImageDescriptionLayout = () => {
    return (
      <div className={classNames('grid', styles['grid-cols-custom'])}>
        <div className={classNames(styles.description)}>
          <Markdown content={filtersList?.description} />
        </div>
        <img
          className={classNames(styles.image)}
          src={getImageUrl(filtersList?.image)}
          alt=""
        />
      </div>
    )
  }

  return (
    <div className={styles.pressReleaseContainer}>
      {pathname.includes('knowledge-center') ? (
        <>
          <div
            className={classNames(styles['knowledge-center-container'], {
              [styles['arabic-knowledge-center-container']]: isArabic(
                language?.code
              ),
            })}
          >
            <div className={styles['top-container']}></div>
            {renderListContent()}
          </div>
          {path.includes('safe-banking') ? renderListItems() : null}
        </>
      ) : (
        renderListContent()
      )}

      {renderPagination()}
    </div>
  )
}

export default MediaPdfsLayout
